/**
 * Client-side PDF Renderer
 *
 * Renders HTML templates to PDF entirely in the browser using html2canvas + jsPDF.
 * This avoids the need for server-side Chromium which doesn't work on static hosting.
 *
 * Uses a hybrid pagination approach:
 * 1. Fast path: If content fits within .page boundaries, render directly
 * 2. Pagination path: If overflow detected, dynamically insert page breaks
 */

import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

// Letter size in points (72 dpi)
const PAGE_WIDTH_POINTS = 612;
const PAGE_HEIGHT_POINTS = 792;

// Usable height after accounting for padding (36px top + 36px bottom)
const PAGE_PADDING = 36;
const PAGE_HEIGHT_USABLE = PAGE_HEIGHT_POINTS - PAGE_PADDING * 2; // 720px

// High resolution scale for better quality
const RENDER_SCALE = 2;

// Minimum content height for a split card part (avoid tiny slivers)
const MIN_SPLIT_HEIGHT = 100;

interface RenderOptions {
  /** Character data to fill into the template */
  characterData: Record<string, string>;
  /** Template HTML content */
  templateHtml: string;
}

interface PaginationResult {
  pages: HTMLElement[];
  usedPagination: boolean;
}

/**
 * Fill template HTML with character data
 * - data-static elements: filled with values (read-only display)
 * - data-field elements: filled with values (would be editable in PDF form)
 */
function fillTemplate(html: string, data: Record<string, string>): string {
  let filled = html;

  // Escape HTML for safe insertion
  const escapeHtml = (str: string) =>
    str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

  // Fill data-static elements
  const dataStaticPattern = /(<[^>]+data-static="([^"]+)"[^>]*>)([^<]*)(<\/[^>]+>)/g;
  filled = filled.replace(dataStaticPattern, (_, openTag, fieldName, _placeholder, closeTag) => {
    const value = data[fieldName];
    if (value !== undefined && value !== null && value !== '') {
      return `${openTag}${escapeHtml(String(value))}${closeTag}`;
    }
    return `${openTag}${closeTag}`;
  });

  // Fill data-field elements (same as static for client-side rendering)
  const dataFieldPattern = /(<[^>]+data-field="([^"]+)"[^>]*>)([^<]*)(<\/[^>]+>)/g;
  filled = filled.replace(dataFieldPattern, (_, openTag, fieldName, _placeholder, closeTag) => {
    const value = data[fieldName];
    if (value !== undefined && value !== null && value !== '') {
      return `${openTag}${escapeHtml(String(value))}${closeTag}`;
    }
    return `${openTag}${closeTag}`;
  });

  return filled;
}

/**
 * Get the header text from a card element
 */
function getCardHeader(card: HTMLElement): string | null {
  const header = card.querySelector('.card-header');
  return header?.textContent || null;
}

/**
 * Pre-process text content by wrapping paragraphs in individual divs.
 * This enables splitting text-heavy cards at paragraph boundaries.
 */
function wrapParagraphsInDivs(container: HTMLElement): boolean {
  // Find elements with pre-wrap text content (like features-content)
  const textContainers = container.querySelectorAll('[style*="pre-wrap"]');

  let modified = false;
  for (const textContainer of textContainers) {
    const text = textContainer.textContent || '';
    // Split on double newlines (paragraph breaks)
    const paragraphs = text.split(/\n\n+/).filter((p) => p.trim());

    if (paragraphs.length > 1) {
      // Replace text content with wrapped divs
      textContainer.innerHTML = paragraphs
        .map((p) => `<div class="feature-item" data-split-item>${p.trim()}</div>`)
        .join('');
      // Remove pre-wrap since we're using divs now
      (textContainer as HTMLElement).style.whiteSpace = 'normal';
      modified = true;
    }
  }

  return modified;
}

/**
 * Split an oversized card into multiple parts that each fit within maxHeight.
 * Returns an array of card elements (part 1, part 2, etc.)
 *
 * The algorithm:
 * 1. Find the content container (everything after the header)
 * 2. Walk through child elements, measuring cumulative height
 * 3. When we exceed maxHeight, create a split point
 * 4. Clone the card for each part, showing only the relevant content
 */
function splitOversizedCard(
  card: HTMLElement,
  _pageTop: number,
  availableHeight: number
): HTMLElement[] {
  const cardRect = card.getBoundingClientRect();
  const cardHeight = cardRect.height;

  // If card fits, no splitting needed
  if (cardHeight <= availableHeight) {
    return [card];
  }

  const headerText = getCardHeader(card);

  // First, try to pre-process text content into splittable paragraphs
  wrapParagraphsInDivs(card);

  // Find all splittable content items within the card
  // Look for common content patterns: feature-item, trait-item, spell-item, list items, divs, paragraphs
  const contentSelectors = [
    '.feature-item',
    '.trait-item',
    '.spell-item',
    '.equipment-item',
    '[data-split-item]', // Generic marker for splittable items
    'li',
    'p',
  ];

  // Get all potential split points (child elements we can break between)
  let splitPoints: HTMLElement[] = [];
  for (const selector of contentSelectors) {
    const items = card.querySelectorAll(selector);
    if (items.length > 0) {
      splitPoints = Array.from(items) as HTMLElement[];
      break;
    }
  }

  // Fallback: if no specific content items found, try direct children of the card
  // (excluding the header)
  if (splitPoints.length === 0) {
    splitPoints = Array.from(card.children).filter(
      (el) => !el.classList.contains('card-header')
    ) as HTMLElement[];
  }

  if (splitPoints.length <= 1) {
    // Can't split - single content block (like a text blob)
    // Just return the original card; it will overflow
    console.warn('[PDF] Card has no splittable content items:', headerText);
    return [card];
  }

  const parts: HTMLElement[] = [];

  // Calculate positions relative to card top
  const headerHeight = card.querySelector('.card-header')?.getBoundingClientRect().height || 0;
  const cardPadding = 32; // Top + bottom padding of card (16px each typically)

  interface ItemInfo {
    element: HTMLElement;
    top: number;
    bottom: number;
    index: number;
  }

  const items: ItemInfo[] = splitPoints.map((el, idx) => {
    const rect = el.getBoundingClientRect();
    return {
      element: el,
      top: rect.top - cardRect.top,
      bottom: rect.bottom - cardRect.top,
      index: idx,
    };
  });

  // Determine split boundaries
  // First part gets availableHeight, subsequent parts get full PAGE_HEIGHT_USABLE
  let currentPartMaxHeight = availableHeight;
  let currentPartStartIdx = 0;
  const partBoundaries: { startIdx: number; endIdx: number }[] = [];

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const itemBottomInPart = item.bottom - (currentPartStartIdx === 0 ? 0 : items[currentPartStartIdx].top - headerHeight);

    // Check if this item exceeds the current part's available height
    const heightSoFar = itemBottomInPart + headerHeight + cardPadding;

    if (heightSoFar > currentPartMaxHeight && i > currentPartStartIdx) {
      // This item doesn't fit - end the current part at the previous item
      partBoundaries.push({
        startIdx: currentPartStartIdx,
        endIdx: i - 1,
      });
      currentPartStartIdx = i;
      currentPartMaxHeight = PAGE_HEIGHT_USABLE; // Subsequent parts get full page height
    }
  }

  // Add the final part
  partBoundaries.push({
    startIdx: currentPartStartIdx,
    endIdx: items.length - 1,
  });

  // If we only ended up with one part, the content is indivisible
  if (partBoundaries.length <= 1) {
    console.warn('[PDF] Card content could not be split further:', headerText);
    return [card];
  }

  // Create card clones for each part
  for (let partNum = 0; partNum < partBoundaries.length; partNum++) {
    const { startIdx, endIdx } = partBoundaries[partNum];
    const clonedCard = card.cloneNode(true) as HTMLElement;

    // Find the cloned split points
    let clonedSplitPoints: HTMLElement[] = [];
    for (const selector of contentSelectors) {
      const items = clonedCard.querySelectorAll(selector);
      if (items.length > 0) {
        clonedSplitPoints = Array.from(items) as HTMLElement[];
        break;
      }
    }
    if (clonedSplitPoints.length === 0) {
      clonedSplitPoints = Array.from(clonedCard.children).filter(
        (el) => !el.classList.contains('card-header')
      ) as HTMLElement[];
    }

    // Hide items outside this part's range
    clonedSplitPoints.forEach((el, idx) => {
      if (idx < startIdx || idx > endIdx) {
        (el as HTMLElement).style.display = 'none';
      }
    });

    // Update header for continuation parts
    if (partNum > 0 && headerText) {
      const header = clonedCard.querySelector('.card-header');
      if (header) {
        header.textContent = `${headerText} (continued)`;
      }
      // Add visual indicator that this continues from previous page
      clonedCard.style.borderTop = '2px dashed #d4a64a';
    }

    // Add visual indicator that content continues on next page
    if (partNum < partBoundaries.length - 1) {
      clonedCard.style.borderBottom = '2px dashed #d4a64a';
    }

    // Mark as a split card part for the pagination algorithm
    clonedCard.dataset.splitPart = String(partNum);
    clonedCard.dataset.splitTotal = String(partBoundaries.length);

    parts.push(clonedCard);
  }

  console.log(`[PDF] Split card "${headerText}" into ${parts.length} parts`);
  return parts;
}

/**
 * Process all oversized cards in the page, splitting them as needed.
 * Returns the modified page with split cards inserted.
 */
function splitOversizedCards(page: HTMLElement, pageTop: number): void {
  const leafBreakables = Array.from(
    page.querySelectorAll('[data-breakable]')
  ).filter((el) => el.querySelectorAll('[data-breakable]').length === 0) as HTMLElement[];

  for (const card of leafBreakables) {
    const rect = card.getBoundingClientRect();
    const cardTop = rect.top - pageTop;
    const cardHeight = rect.height;

    // Calculate available height for this card
    // First card on page gets remaining space, others get full page height
    const availableHeight = PAGE_HEIGHT_USABLE - (cardTop % PAGE_HEIGHT_USABLE);

    // Check if this card needs splitting
    if (cardHeight > availableHeight + MIN_SPLIT_HEIGHT) {
      const parts = splitOversizedCard(card, pageTop, availableHeight);

      if (parts.length > 1) {
        // Replace the original card with the split parts
        const parent = card.parentElement;
        if (parent) {
          for (const part of parts) {
            parent.insertBefore(part, card);
          }
          card.remove();
        }
      }
    }
  }
}

/**
 * Detect if any .page element has content that overflows its usable height
 */
function detectOverflow(container: HTMLElement): boolean {
  const pages = container.querySelectorAll('.page');
  for (const page of pages) {
    // scrollHeight includes content that overflows
    if (page.scrollHeight > PAGE_HEIGHT_USABLE + PAGE_PADDING * 2) {
      return true;
    }
  }
  return false;
}

/**
 * Paginate content by measuring ALL breakable elements and determining
 * which page they belong on based on their position from the page top.
 *
 * This algorithm:
 * 1. Finds ALL elements with data-breakable (including nested ones)
 * 2. Measures each element's position relative to the page top
 * 3. Assigns each element to a page number
 * 4. For each page: clones the entire page and hides elements not on that page
 */
function paginateContent(container: HTMLElement): HTMLElement[] {
  const originalPages = Array.from(container.querySelectorAll('.page')) as HTMLElement[];
  const allPages: HTMLElement[] = [];

  for (const originalPage of originalPages) {
    const pageRect = originalPage.getBoundingClientRect();
    const pageTop = pageRect.top + PAGE_PADDING; // Content starts after top padding

    // FIRST: Split any oversized cards into multiple parts
    // This must happen before we measure and assign page numbers
    splitOversizedCards(originalPage, pageTop);

    // Find ALL breakable elements (including nested ones) in document order
    // Filter to only get "leaf" breakables (cards, not section-groups that contain cards)
    const allBreakables = Array.from(
      originalPage.querySelectorAll('[data-breakable]')
    ) as HTMLElement[];

    // Filter to leaf breakables only (elements that don't contain other breakables)
    const leafBreakables = allBreakables.filter(
      (el) => el.querySelectorAll('[data-breakable]').length === 0
    );

    if (leafBreakables.length === 0) {
      // No breakable elements, use page as-is
      allPages.push(originalPage);
      continue;
    }

    // Calculate position info for each breakable element
    interface BreakableInfo {
      element: HTMLElement;
      top: number; // Position from page content top
      bottom: number;
      pageNumber: number;
    }

    const breakableInfos: BreakableInfo[] = leafBreakables.map((el) => {
      const rect = el.getBoundingClientRect();
      return {
        element: el,
        top: rect.top - pageTop,
        bottom: rect.bottom - pageTop,
        pageNumber: 0, // Will be assigned below
      };
    });

    // Assign page numbers to each element
    let currentPageNumber = 0;
    let currentPageBottom = PAGE_HEIGHT_USABLE;

    for (const info of breakableInfos) {
      // If this element's bottom exceeds current page, it starts a new page
      if (info.bottom > currentPageBottom) {
        currentPageNumber++;
        // New page boundary starts from this element's top
        currentPageBottom = info.top + PAGE_HEIGHT_USABLE;
      }
      info.pageNumber = currentPageNumber;
    }

    const totalPages = currentPageNumber + 1;

    // Get the section name for "(continued)" headers
    const getSectionNameForElement = (el: HTMLElement): string | null => {
      // Walk up to find parent with data-section-name
      let current: HTMLElement | null = el;
      while (current && current !== originalPage) {
        if (current.dataset?.sectionName) {
          return current.dataset.sectionName;
        }
        current = current.parentElement;
      }
      return null;
    };

    // Create pages by cloning and hiding elements
    for (let pageNum = 0; pageNum < totalPages; pageNum++) {
      const clonedPage = originalPage.cloneNode(true) as HTMLElement;

      // Find all leaf breakables in the clone
      const clonedBreakables = Array.from(
        clonedPage.querySelectorAll('[data-breakable]')
      ).filter((el) => el.querySelectorAll('[data-breakable]').length === 0) as HTMLElement[];

      // Hide elements that don't belong on this page
      clonedBreakables.forEach((clonedEl, idx) => {
        if (breakableInfos[idx].pageNumber !== pageNum) {
          clonedEl.style.display = 'none';
        }
      });

      // Clean up empty containers (section-groups with all children hidden)
      const sectionGroups = clonedPage.querySelectorAll('.section-group');
      sectionGroups.forEach((group) => {
        const visibleCards = group.querySelectorAll('[data-breakable]:not([style*="display: none"])');
        // Check if there are any visible leaf breakables
        const hasVisibleContent = Array.from(visibleCards).some(
          (card) => card.querySelectorAll('[data-breakable]').length === 0
        );
        if (!hasVisibleContent) {
          (group as HTMLElement).style.display = 'none';
        }
      });

      // For pages after the first, add "(continued)" header
      if (pageNum > 0) {
        // Find the first visible element on this page
        const firstVisibleIdx = breakableInfos.findIndex((info) => info.pageNumber === pageNum);
        if (firstVisibleIdx >= 0) {
          const sectionName = getSectionNameForElement(breakableInfos[firstVisibleIdx].element);
          if (sectionName) {
            const continued = document.createElement('div');
            continued.className = 'section-continued';
            continued.textContent = `${sectionName} (continued)`;
            clonedPage.insertBefore(continued, clonedPage.firstChild);
          }
        }
      }

      // Hide the header on continuation pages (it should only appear on page 1)
      if (pageNum > 0) {
        const header = clonedPage.querySelector('.header');
        if (header) {
          (header as HTMLElement).style.display = 'none';
        }
      }

      allPages.push(clonedPage);
    }
  }

  return allPages;
}

/**
 * Main pagination function - uses fast path or pagination path as needed
 */
async function paginateIfNeeded(container: HTMLElement): Promise<PaginationResult> {
  // Wait for fonts to load before measuring
  await document.fonts.ready;

  if (!detectOverflow(container)) {
    // Fast path - no pagination needed
    const pages = Array.from(container.querySelectorAll('.page')) as HTMLElement[];
    return { pages, usedPagination: false };
  }

  // Pagination path - dynamically split content
  console.log('[PDF] Content overflow detected, using dynamic pagination');
  const pages = paginateContent(container);

  // Remove original pages and add new paginated pages to the container
  // This is required because html2canvas needs elements in the DOM
  const originalPages = container.querySelectorAll('.page');
  originalPages.forEach((page) => page.remove());
  pages.forEach((page) => container.appendChild(page));

  return { pages, usedPagination: true };
}

/**
 * Render an array of page elements to PDF
 */
async function renderPagesToPdf(pages: HTMLElement[]): Promise<Uint8Array> {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'pt',
    format: 'letter',
  });

  for (let i = 0; i < pages.length; i++) {
    const pageElement = pages[i];

    // Render page to canvas
    const canvas = await html2canvas(pageElement, {
      scale: RENDER_SCALE,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      width: PAGE_WIDTH_POINTS,
      height: PAGE_HEIGHT_POINTS,
      windowWidth: PAGE_WIDTH_POINTS,
      windowHeight: PAGE_HEIGHT_POINTS,
    });

    // Add new page if not the first
    if (i > 0) {
      pdf.addPage('letter', 'portrait');
    }

    // Add canvas as image to PDF
    const imgData = canvas.toDataURL('image/jpeg', 0.95);
    pdf.addImage(imgData, 'JPEG', 0, 0, PAGE_WIDTH_POINTS, PAGE_HEIGHT_POINTS);
  }

  const pdfArrayBuffer = pdf.output('arraybuffer');
  return new Uint8Array(pdfArrayBuffer);
}

/**
 * Render HTML template to PDF using html2canvas + jsPDF
 *
 * Uses hybrid pagination:
 * - Fast path: If content fits within .page boundaries, render directly
 * - Pagination path: If overflow detected, dynamically insert page breaks
 *   at elements marked with data-breakable attribute
 */
export async function renderHtmlToPdf(options: RenderOptions): Promise<Uint8Array> {
  const { characterData, templateHtml } = options;

  // Fill template with data
  const filledHtml = fillTemplate(templateHtml, characterData);

  // Create a hidden container for rendering
  const container = document.createElement('div');
  container.style.cssText = `
    position: fixed;
    left: -9999px;
    top: 0;
    width: ${PAGE_WIDTH_POINTS}px;
    background: white;
  `;
  container.innerHTML = filledHtml;
  document.body.appendChild(container);

  try {
    // Check for overflow and paginate if needed
    const { pages } = await paginateIfNeeded(container);

    if (pages.length === 0) {
      throw new Error('No .page elements found in template');
    }

    // Render pages to PDF
    return await renderPagesToPdf(pages);
  } finally {
    // Clean up
    document.body.removeChild(container);
  }
}

/**
 * Load HTML template from public assets
 */
export async function loadHtmlTemplate(templateId: string): Promise<string> {
  // Map template IDs to their HTML file paths
  const templatePaths: Record<string, string> = {
    'ignus-classic': '/mockups/ignus-template-classic.html',
    'ignus-compact': '/mockups/ignus-template-compact.html',
    'srd-classic': '/mockups/srd-template-classic.html',
    'srd-compact': '/mockups/srd-template-compact.html',
  };

  const templatePath = templatePaths[templateId];
  if (!templatePath) {
    throw new Error(`Unknown template: ${templateId}`);
  }

  const response = await fetch(templatePath);
  if (!response.ok) {
    throw new Error(`Failed to load template: ${response.status}`);
  }

  return response.text();
}
